import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

/**
 * Service helper for form functionality.
 */
@Injectable({
  providedIn: 'root',
})
export class FormService {
  /**
   * Force validation on a form.
   * @param form The form to check.
   * @param scrollTo Whether we should scroll the user to the next field with an error.
   */
  public validate(form: UntypedFormGroup, scrollTo = true): void {
    Object.keys(form.controls).forEach((field) => {
      const control = form.get(field);

      if (control) {
        control.markAsTouched({ onlySelf: true });
        control.updateValueAndValidity();
      }
    });

    if (scrollTo) {
      this.scrollToError();
    }
  }

  /**
   * Scroll to an element/field within the form.
   * @param el Element to scroll to.
   */
  scrollTo(el: Element): void {
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /**
   * Scroll to the next element/field with an error.
   */
  scrollToError(): void {
    setTimeout(() => {
      const firstElementWithError = document.querySelector('.is-invalid');

      if (firstElementWithError) {
        this.scrollTo(firstElementWithError);
      }
    });
  }

  /**
   * Focus on an input element within a larger element component (ensuring we always focus on the last one found).
   * @param elementId Element ID.
   */
  focusOnLastInput(elementId: string): void {
    setTimeout(() => {
      const foundElements = document.querySelectorAll(`#${elementId} input, #${elementId} textarea`);
      const lastElement = foundElements[foundElements.length - 1] as HTMLElement;
      lastElement.focus();
    });
  }
}
